import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';
import './ClientSelector.scss';

interface ClientSelectorProps {
  clientIndex: number;
  dropdownId: string;
  activeDropdownId: string | null;
  onToggleDropdown: (dropdownId: string, event: React.MouseEvent) => void;
  onSelectClient: (clientIndex: number) => void;
  numPeople?: number;
  formatClientLabel?: (clientIndex: number) => string;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  clientIndex,
  dropdownId,
  activeDropdownId,
  onToggleDropdown,
  onSelectClient,
  numPeople = 50,
  formatClientLabel
}) => {
  const isOpen = activeDropdownId === dropdownId;
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number, width: number} | null>(null);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen) {
      const toggleButton = document.querySelector(`[data-dropdown-id="${dropdownId}"]`) as HTMLElement;
      if (toggleButton) {
        const rect = toggleButton.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen, dropdownId]);

  const formatLabel = (index: number) => {
    if (formatClientLabel) return formatClientLabel(index);
    return index === -1 ? 'Grupo' : `Cliente ${index + 1}`;
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    onToggleDropdown(dropdownId, e);
  };

  return (
    <div className="client-selector">
      {activeDropdownId && activeDropdownId !== dropdownId ? (
        // Show only text when another dropdown is active
        <div className="client-text-display">
          {formatLabel(clientIndex)}
        </div>
      ) : (
        // Show custom dropdown only when this is the active one or no dropdown is active
        <div className="custom-dropdown-container">
          <button
            className={`custom-dropdown-toggle ${isOpen ? 'active' : ''}`}
            data-dropdown-id={dropdownId}
            onClick={handleToggleClick}
          >
            {formatLabel(clientIndex)}
            <FaChevronDown className="dropdown-arrow" />
          </button>
          
          {/* Portal-based dropdown menu - renders at document level */}
          {isOpen && dropdownPosition && createPortal(
            <div 
              className="portal-dropdown-menu"
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 99999999,
                background: 'var(--card-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                padding: '0.5rem 0',
                maxHeight: '200px',
                overflowY: 'auto',
                minWidth: '120px'
              }}
            >
              {/* Grupo option */}
              <button
                className={`dropdown-item ${clientIndex === -1 ? 'active' : ''}`}
                onClick={() => onSelectClient(-1)}
              >
                {formatLabel(-1)}
              </button>
              
              {/* Client options */}
              {Array.from({ length: numPeople }, (_, i) => i).map((currentIndex) => (
                <button
                  key={currentIndex}
                  className={`dropdown-item ${clientIndex === currentIndex ? 'active' : ''}`}
                  onClick={() => onSelectClient(currentIndex)}
                >
                  {formatLabel(currentIndex)}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
